import { useContext, useMemo, useRef } from "react";

import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { computeTaskSubjectTexts, type MessageText, taskSubjectTextCollections } from "./dispatchItems";
import { isTmsEventItemId, type TaskFold, taskItemIdOf } from "./tasks";
import { useMessageContentDownload } from "./useMessageContentDownload";

/** Stable fallback while the compute's first run is pending, while tasks are off, and while no task names a message. */
const NO_TEXTS: ReadonlyMap<string, MessageText> = new Map();

/**
 * What each open task's message or event reads, by its Dispatch item id, recomputed as the store changes; empty while
 * the workspace shows no tasks.
 *
 * The map keeps its identity while what it holds is unchanged, so a write to an item no task names re-renders nothing
 * that reads it. The driver grain builds no item list that would download a message's content, so each open task's
 * message downloads its own.
 *
 * @param workspaceId - The workspace whose items the downloads read.
 * @param tasks - The workspace's open tasks.
 * @param enabled - Whether the workspace shows tasks.
 */
export function useTaskSubjectTexts(
  workspaceId: string,
  tasks: TaskFold,
  enabled: boolean,
): ReadonlyMap<string, MessageText> {
  const { myAccount } = useContext(MyAccountContext);
  const language = myAccount?.preferredLanguage ?? "en";
  const computed = useCollectionsCompute(
    taskSubjectTextCollections,
    () => (enabled && tasks.open.length > 0 ? computeTaskSubjectTexts(tasks, language) : null),
    [enabled, tasks, language],
    "Dispatch:taskSubjects",
  );
  const previous = useRef(NO_TEXTS);
  const texts = useMemo(() => {
    const next = computed && computed.size > 0 ? computed : NO_TEXTS;
    if (!sameTexts(previous.current, next)) previous.current = next;
    return previous.current;
  }, [computed]);

  const messageIdsKey = useMemo(() => {
    if (!enabled) return "";
    const ids = new Set<string>();
    for (const open of tasks.open) {
      const id = taskItemIdOf(open);
      // An event's text is on its row; only a message's content is downloaded.
      if (id !== null && !isTmsEventItemId(id)) ids.add(id);
    }
    return [...ids].join(",");
  }, [enabled, tasks]);
  useMessageContentDownload(workspaceId, messageIdsKey, language, { what: "Task message" });

  return texts;
}

/** Whether two subject maps hold the same ids with the same text and photo flag. */
function sameTexts(a: ReadonlyMap<string, MessageText>, b: ReadonlyMap<string, MessageText>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [id, text] of a) {
    const other = b.get(id);
    if (!other || other.text !== text.text || other.photo !== text.photo) return false;
  }
  return true;
}
